import {
  Box,
  ScrollArea,
  Button,
  Container,
  Grid,
  Text,
} from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import { Dispatch, SetStateAction } from "react";
import useSWRInfinite from "swr/infinite";
import WatsonApi from "../../../backends/WatsonApi";
import { Channel } from "./Channel";
import { MessagingListRulesQuery } from "../../modals/Messaging/MessagingListRules";
import RoundIcon from "../../admin/utilities/RoundIcon";
import styles from "../styles/ChannelsList.module.scss";
import classNames from "classnames";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { useStores } from "../../../stores/useStores";

export type IChannel = {
  id: number;
  title: string;
  description: string;
  type: "message" | "broadcast";
  branding: {
    type: "image" | "fontawesome" | "text";
    alt?: string;
    value: string;
  };
  timestamp: string;
  has_unread: boolean;
  owner_user_id: string;
};

export function useChannels(initialChannels) {
  return useSWRInfinite(
    (pageIndex) =>
      JSON.stringify({
        page: pageIndex,
        key: "message-channels",
      }),
    async (key) => {
      const parsedKey = JSON.parse(key);
      const client = await WatsonApi();
      const nextPage = parsedKey.page + 1;
      const result = await client.apis.message_list.message_list_list({
        exclude_owner_org: "root",
        page: nextPage,
      });
      const parsed = JSON.parse(result.data);
      return parsed.data;
    },
    {
      fallbackData: [initialChannels],
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateFirstPage: false,
    },
  );
}

export default function ChannelsList(props: {
  initialChannels: IChannel[];
  totalChannels: number;
  currentChannel: IChannel | undefined;
  setCurrentChannel: Dispatch<SetStateAction<IChannel | undefined>>;
  showConversation: boolean;
  setRecipientUsers: Dispatch<SetStateAction<IUser[]>>;
  setRecipientRules: Dispatch<SetStateAction<MessagingListRulesQuery[]>>;
  setShowConversation: Dispatch<SetStateAction<boolean>>;
}) {
  const { userStore } = useStores();

  const { data, isLoading, setSize, isValidating, mutate } = useChannels(
    props.initialChannels,
  );

  const channels = data?.reduce((acc, curr) => [...acc, ...curr], []) || [];

  const isMoreChannels = !isLoading && props.totalChannels > channels.length;

  const containerClassName = classNames({
    [styles.container]: true,
    [styles.containerHidden]: !!props.currentChannel,
  });

  useAsyncEffect(async () => {
    if (props.currentChannel?.has_unread) {
      const client = await WatsonApi();
      await client.apis.users.users_activity_mark_all_read({
        user_pk: userStore.id,
        data: {
          message_list_id: props.currentChannel.id,
        },
      });
      if (data) {
        await mutate(
          data.map((page) =>
            page.map((c) => {
              if (c.id === (props.currentChannel as IChannel).id) {
                return { ...c, has_unread: false };
              } else {
                return c;
              }
            }),
          ),
        );
      }
    }
  }, [props.currentChannel]);

  return (
    <Box
      flexBasis={{
        initial: "100%",
        md: props.showConversation ? "50%" : "100%",
      }}
      height={"500px"}
      style={{ backgroundColor: "var(--gray-2)", boxShadow: "var(--shadow-4)" }}
      p={"2"}
      className={containerClassName}
    >
      <Grid rows={"2"} height={"100%"} style={{ gridTemplateRows: "1fr auto" }}>
        {channels.length > 0 ? (
          <ScrollArea scrollbars={"vertical"}>
            <Container mr={"5"}>
              {channels.map((list) => {
                return (
                  <Channel
                    channel={list}
                    currentChannelId={props.currentChannel?.id}
                    setCurrentChannel={(channel) => {
                      props.setCurrentChannel(channel);
                      props.setShowConversation(!!channel);
                      props.setRecipientUsers([]);
                      props.setRecipientRules([]);
                    }}
                    key={list.id}
                  />
                );
              })}
            </Container>
          </ScrollArea>
        ) : (
          <Box style={{ textAlign: "center" }}>
            <Box>
              <RoundIcon className={styles.roundButton}>
                <FontAwesome prefix={"fas"} name={"fa-envelope"} />
              </RoundIcon>
            </Box>
            <Box pt={"1"}>
              <Text>You do not have any messages.</Text>
            </Box>
          </Box>
        )}
        {isMoreChannels && (
          <Box pt={"3"} style={{ textAlign: "center" }}>
            <Button
              loading={isValidating}
              onClick={() => setSize((s) => s + 1)}
            >
              Load More
            </Button>
          </Box>
        )}
      </Grid>
    </Box>
  );
}
